import { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Grid from '../components/grid';
import ProductGridItems from '../components/layout/product-grid-items';
import Collections from '../components/layout/search/collections';
import FilterList from '../components/layout/search/filter';
import { defaultSort, sorting } from '../lib/constants';
import type { Product } from '../lib/burokshop/types';
import {
  getAllProducts,
  getProductsByCategorySlug,
  getFirstImageByProductIds,
} from '../lib/maison-adji-api';
import { usePreferredCurrency } from '../context/CurrencySettingsContext';
import { useLanguage } from '../context/LanguageContext';

/** Mappe un produit Maison Adji + URL image vers le type Product de la grille */
function toGridProduct(
  p: { id: string; name: string; slug: string; price_xof: number | null; price_eur: number | null },
  imageUrl: string,
  currencyCode: 'XOF' | 'EUR'
): Product {
  const amount = currencyCode === 'EUR' ? (p.price_eur ?? p.price_xof ?? 0) : (p.price_xof ?? p.price_eur ?? 0);
  return {
    id: p.id,
    handle: p.slug,
    availableForSale: true,
    title: p.name,
    description: '',
    descriptionHtml: '',
    options: [],
    priceRange: {
      maxVariantPrice: { amount: String(amount), currencyCode },
      minVariantPrice: { amount: String(amount), currencyCode },
    },
    variants: [],
    featuredImage: { url: imageUrl, altText: p.name, width: 400, height: 400 },
    images: [],
    seo: { title: p.name, description: '' },
    tags: [],
    updatedAt: '',
  };
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { preferredCurrency } = usePreferredCurrency();
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const collection = params.collection || '';

  useEffect(() => {
    async function loadProducts() {
      setLoading(true);
      const sort = searchParams.get('sort') || '';
      const searchValue = searchParams.get('q') || '';
      const { sortKey, reverse } = sorting.find((item) => item.slug === sort) || defaultSort;

      let list;
      if (collection) {
        list = await getProductsByCategorySlug(collection);
      } else {
        list = await getAllProducts({
          query: searchValue || undefined,
          sortKey: sortKey === 'PRICE' ? 'price' : 'created_at',
          reverse: !!reverse,
        });
      }

      const ids = list.map((p) => p.id);
      const firstImages = await getFirstImageByProductIds(ids);
      const currencyCode = preferredCurrency;
      const gridProducts = list.map((p) =>
        toGridProduct(p, firstImages[p.id] ?? '', currencyCode)
      );
      setProducts(gridProducts);
      setLoading(false);
    }
    loadProducts();
  }, [searchParams, preferredCurrency, collection]);

  const searchValue = searchParams.get('q') || '';
  const resultsText = products.length > 1 ? 'résultats' : 'résultat';

  if (loading) {
    return (
      <div className="mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 pt-24 pb-4 text-black md:flex-row dark:text-white">
        <div className="order-first w-full flex-none md:max-w-[125px]">
          <div className="mb-3 h-4 w-5/6 animate-pulse rounded-sm bg-neutral-400 dark:bg-neutral-700" />
        </div>
        <div className="order-last min-h-screen w-full md:order-none">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-lg">Un instant...</div>
          </div>
        </div>
        <div className="order-none flex-none md:order-last md:w-[125px]">
          <div className="mb-3 h-4 w-5/6 animate-pulse rounded-sm bg-neutral-400 dark:bg-neutral-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 pt-24 pb-4 text-black md:flex-row dark:text-white">
      <div className="order-first w-full flex-none md:max-w-[125px]">
        <Collections />
      </div>

      <div className="order-last min-h-screen w-full md:order-none">
        {searchValue ? (
          <p className="mb-4">
            {products.length === 0
              ? 'Aucun produit ne correspond à '
              : `Affichage de ${products.length} ${resultsText} pour `}
            <span className="font-bold">&quot;{searchValue}&quot;</span>
          </p>
        ) : null}
        {products.length > 0 ? (
          <Grid className="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            <ProductGridItems products={products} />
          </Grid>
        ) : (
          <div className="py-12 text-center">
            <p className="text-lg text-neutral-500 dark:text-neutral-400">
              Aucun produit trouvé
            </p>
          </div>
        )}
      </div>

      <div className="order-none flex-none md:order-last md:w-[125px]">
        <FilterList list={sorting} title={t('sort.title')} />
      </div>
    </div>
  );
}
